/**
 * Frontend Numerology Calculation Engine  (ES Module)
 * Mirror of server/utils/numerologyEngine.js — runs entirely in the browser.
 * Called on-the-fly whenever a report is displayed, so we never need to
 * persist calculated data in MongoDB.
 */

// ── Helpers ────────────────────────────────────────────────────────────────

/** Reduce a number to single digit; optionally keep master numbers 11, 22, 33 */
const reduceNumber = (num, keepMaster = false) => {
    while (num > 9) {
        if (keepMaster && (num === 11 || num === 22 || num === 33)) break;
        num = String(num).split('').reduce((acc, d) => acc + parseInt(d), 0);
    }
    return num;
};

/** Sum all digit characters in a string */
const sumDigits = (str) =>
    str.replace(/\D/g, '').split('').reduce((acc, d) => acc + parseInt(d), 0);

// ── Core Calculations ──────────────────────────────────────────────────────

/**
 * Bhagyank (Life Path) — Method 2:
 * Day, Month, Year each reduced independently; results summed & reduced.
 * Always returns a SINGLE digit (1–9). No master numbers preserved.
 */
export const calcBhagyank = (dob) => {
    const d = new Date(dob);
    const day = d.getUTCDate();
    const month = d.getUTCMonth() + 1;
    const year = d.getUTCFullYear();

    const dayR = reduceNumber(sumDigits(String(day)), false);
    const monthR = reduceNumber(sumDigits(String(month)), false);
    const yearR = reduceNumber(sumDigits(String(year)), false);

    return reduceNumber(dayR + monthR + yearR, false);
};

/** Conductor / Mulank — reduced day of birth */
export const calcConductor = (dob) => {
    const day = new Date(dob).getUTCDate();
    return reduceNumber(sumDigits(String(day)), false);
};

/**
 * Kua Number (Feng Shui / Ba Gua Number)
 * Exact formula from image:
 *
 * STEP 1 — Year digit sum → reduce to single digit
 *
 * BEFORE 2000:
 *   Male:   kuaNum = 11 − yearDigitSum   (if > 9, reduce again)
 *   Female: kuaNum = yearDigitSum + 4     (if > 9, reduce again)
 *
 * AFTER 2000 (year >= 2000):
 *   Male:   kuaNum = 10 − yearDigitSum   (if > 9, reduce again)
 *   Female: kuaNum = yearDigitSum + 5     (if > 9, reduce again)
 *
 * Special case: if result is 5 → Male becomes 2, Female becomes 8
 */
export const calcKuaNumber = (dob, gender) => {
    const d = new Date(dob);
    const year = d.getUTCFullYear();

    // Step 1: Sum all digits of year, reduce to single digit
    let yearSum = reduceNumber(sumDigits(String(year)), false);

    let kua;
    const isMale = gender === 'male';
    const isAfter2000 = year >= 2000;

    if (isAfter2000) {
        // After 2000
        if (isMale) {
            kua = 10 - yearSum;
        } else {
            kua = yearSum + 5;
        }
    } else {
        // Before 2000
        if (isMale) {
            kua = 11 - yearSum;
        } else {
            kua = yearSum + 4;
        }
    }

    // Reduce to single digit if two digits
    kua = reduceNumber(kua, false);

    // Special case: 5 is not a Kua number
    if (kua === 5) {
        kua = isMale ? 2 : 8;
    }

    return kua;
};

/** Lo Shu Grid — digit count (1–9) from DOB string */
export const calcLoshuGrid = (dob) => {
    const d = new Date(dob);
    const dobStr = `${d.getUTCDate()}${d.getUTCMonth() + 1}${d.getUTCFullYear()}`;
    const grid = {};
    for (let i = 1; i <= 9; i++) grid[i] = 0;
    dobStr.split('').forEach(ch => {
        const n = parseInt(ch);
        if (n >= 1 && n <= 9) grid[n]++;
    });
    return grid;
};

/** Missing numbers (1–9 absent from DOB) */
export const calcMissingNumbers = (loshuGrid) =>
    Object.keys(loshuGrid).filter(k => loshuGrid[k] === 0).map(Number);

// ── Vedic Grid ─────────────────────────────────────────────────────────────

const VEDIC_PLANETS = {
    1: 'Sun (Surya)', 2: 'Moon (Chandra)', 3: 'Jupiter (Guru)',
    4: 'Rahu', 5: 'Mercury (Budh)', 6: 'Venus (Shukra)',
    7: 'Ketu', 8: 'Saturn (Shani)', 9: 'Mars (Mangal)',
};

export const calcVedicGrid = (loshuGrid) => {
    const vedicGrid = {};
    Object.keys(loshuGrid).forEach(k => {
        vedicGrid[k] = { count: loshuGrid[k], planet: VEDIC_PLANETS[k], present: loshuGrid[k] > 0 };
    });
    return vedicGrid;
};

// ── Lucky / Unlucky / Neutral Number Associations ──────────────────────────

const MULANK_NUMBER_ASSOCIATIONS = {
    1: { lucky: [1, 2, 3, 5, 6, 9], unlucky: [8], neutral: [4, 7] },
    2: { lucky: [1, 2, 3, 5], unlucky: [4, 8, 9], neutral: [7, 6] },
    3: { lucky: [1, 2, 3, 5, 7], unlucky: [6], neutral: [4, 8, 9] },
    4: { lucky: [1, 5, 6, 7], unlucky: [2, 9], neutral: [3] },
    5: { lucky: [1, 2, 3, 5, 6], unlucky: [], neutral: [4, 7, 8, 9] },
    6: { lucky: [1, 4, 5, 7], unlucky: [3], neutral: [2, 8, 9] },
    7: { lucky: [1, 3, 4, 5, 6], unlucky: [], neutral: [2, 8, 9] },
    8: { lucky: [3, 5, 6, 7], unlucky: [1, 2], neutral: [9] },
    9: { lucky: [1, 3, 5], unlucky: [2, 4], neutral: [6, 7, 8, 9] },
};

export const calcCombinedAssociations = (mulank, bhagyank) => {
    const mulankBase = mulank > 9 ? reduceNumber(mulank) : mulank;
    const bhagyankBase = bhagyank > 9 ? reduceNumber(bhagyank) : bhagyank;

    const mA = MULANK_NUMBER_ASSOCIATIONS[mulankBase] || MULANK_NUMBER_ASSOCIATIONS[1];
    const bA = MULANK_NUMBER_ASSOCIATIONS[bhagyankBase] || MULANK_NUMBER_ASSOCIATIONS[1];

    const allNeutral = [...new Set([...(mA.neutral || []), ...(bA.neutral || [])])];

    const combinedLucky = mA.lucky
        .filter(n => bA.lucky.includes(n))
        .filter(n => !allNeutral.includes(n));

    const luckyButNotBhagyank = mA.lucky
        .filter(n => !bA.lucky.includes(n))
        .filter(n => !allNeutral.includes(n));

    const combinedEnemy = [...new Set([...luckyButNotBhagyank, ...mA.unlucky])].sort((a, b) => a - b);

    return { lucky: combinedLucky, unlucky: combinedEnemy, neutral: allNeutral };
};

// ── Lucky Colours & Days ───────────────────────────────────────────────────

const LUCKY_COLOURS = {
    1: ['Red', 'Orange', 'Yellow'],
    2: ['White', 'Cream', 'Silver'],
    3: ['Yellow', 'Purple', 'Pink'],
    4: ['Blue', 'Grey', 'Electric Blue'],
    5: ['Green', 'Light Green', 'Turquoise'],
    6: ['White', 'Light Blue', 'Green', 'Pink'],
    7: ['Grey', 'Off-White', 'Light Yellow'],
    8: ['Black', 'Navy Blue', 'Charcoal'],
    9: ['Red', 'Maroon', 'Burgundy'],
};

const LUCKY_DAYS = {
    1: ['Sunday'], 2: ['Monday'], 3: ['Thursday'],
    4: ['Sunday'], 5: ['Wednesday'], 6: ['Friday'],
    7: ['Monday'], 8: ['Saturday'], 9: ['Tuesday'],
};

export const calcLuckyColours = (conductor) => {
    const base = conductor > 9 ? reduceNumber(conductor) : conductor;
    return LUCKY_COLOURS[base] || LUCKY_COLOURS[1];
};

export const calcLuckyDays = (conductor) => {
    const base = conductor > 9 ? reduceNumber(conductor) : conductor;
    return LUCKY_DAYS[base] || LUCKY_DAYS[1];
};

// ── Dasha Chart ────────────────────────────────────────────────────────────

const DASHA_LORDS = [
    { lord: 'Sun', years: 6 },
    { lord: 'Moon', years: 10 },
    { lord: 'Mars', years: 7 },
    { lord: 'Rahu', years: 18 },
    { lord: 'Jupiter', years: 16 },
    { lord: 'Saturn', years: 19 },
    { lord: 'Mercury', years: 17 },
    { lord: 'Ketu', years: 7 },
    { lord: 'Venus', years: 20 },
];

const DASHA_COLOURS = {
    Sun: '#FF6B00', Moon: '#C0C0C0', Mars: '#FF3333', Rahu: '#4B0082',
    Jupiter: '#FFD700', Saturn: '#2F4F4F', Mercury: '#32CD32',
    Ketu: '#8B4513', Venus: '#FF69B4',
};

export const calcDashaChart = (dob) => {
    const birthDate = new Date(dob);
    const bhagyank = calcBhagyank(dob);
    const startIdx = (bhagyank - 1) % 9;
    const dashas = [];
    let currentDate = new Date(birthDate);

    for (let i = 0; i < 9; i++) {
        const idx = (startIdx + i) % 9;
        const dasha = DASHA_LORDS[idx];
        const endDate = new Date(currentDate);
        endDate.setFullYear(endDate.getFullYear() + dasha.years);

        const now = new Date();
        const isActive = now >= currentDate && now < endDate;
        const progress = isActive
            ? Math.round(((now - currentDate) / (endDate - currentDate)) * 100)
            : (now >= endDate ? 100 : 0);

        dashas.push({
            lord: dasha.lord,
            years: dasha.years,
            startDate: currentDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            isActive,
            progress,
            colour: DASHA_COLOURS[dasha.lord],
        });
        currentDate = new Date(endDate);
    }
    return dashas;
};



// ── Master Function ────────────────────────────────────────────────────────

/**
 * calculateAll({ dob, gender, mobileNumber? })
 * Returns every derived numerology value.
 * Call this on the frontend whenever you need to display a report.
 */
export const calculateAll = ({ dob, gender }) => {
    const bhagyank = calcBhagyank(dob);
    const conductor = calcConductor(dob);
    const loshuGrid = calcLoshuGrid(dob);
    const vedicGrid = calcVedicGrid(loshuGrid);
    const missingNumbers = calcMissingNumbers(loshuGrid);
    const combinedAssoc = calcCombinedAssociations(conductor, bhagyank);
    const luckyColours = calcLuckyColours(conductor);
    const luckyDays = calcLuckyDays(conductor);
    const dashaChart = calcDashaChart(dob);
    const kuaNumber = calcKuaNumber(dob, gender);

    return {
        bhagyank,
        conductor,
        mulank: conductor,    // 'mulank' is the schema alias for conductor
        loshuGrid,
        vedicGrid,
        missingNumbers,
        luckyNumbers: combinedAssoc.lucky,
        unluckyNumbers: combinedAssoc.unlucky,
        neutralNumbers: combinedAssoc.neutral,
        luckyColours,
        luckyDays,
        dashaChart,
        kuaNumber,
    };
};

export { reduceNumber };
